import { createLogic } from 'redux-logic';
import ActionConstants from '../constants/actions';
import ApiConstants from '../constants/apiConstants';
import http from '../utils/http';
import { extractErrorMessage } from '../utils/errorHelper';

const getAllUsersLogic = createLogic({
	type: ActionConstants.UsersConstants.GET_ALL_USERS,
	latest: true,
	process({ action }, dispatch, done) {
		dispatch({ type: ActionConstants.UsersConstants.GET_ALL_USERS_INIT });

		const brandId = window?.LTD?.getSiteInfo?.()?.brandId;
		const offset = action?.payload?.offset ?? 0;
		const size = action?.payload?.size ?? 10;
		const searchParam = action?.payload?.searchParam;

		if (!brandId) {
			dispatch({
				type: ActionConstants.UsersConstants.GET_ALL_USERS_ERROR,
				payload: 'brandId is required',
			});
			done();
			return;
		}

		const queryParams = { offset, size };
		if (searchParam) {
			queryParams.searchParam = searchParam;
		}

		const url = ApiConstants.USERS.GET_ALL.URL(brandId);
		return http
			.get(url, {}, queryParams)
			.then((res) => {
				dispatch({
					type: ActionConstants.UsersConstants.GET_ALL_USERS_SUCCESS,
					payload: res,
				});
				done();
			})
			.catch((err) => {
				const message = extractErrorMessage(err, 'Failed to fetch users');
				dispatch({
					type: ActionConstants.UsersConstants.GET_ALL_USERS_ERROR,
					payload: String(message),
				});
				done();
			});
	},
	warnTimeout: 0,
});

const updateUserStatusLogic = createLogic({
	type: ActionConstants.UsersConstants.UPDATE_USER_STATUS,
	latest: true,
	process({ action }, dispatch, done) {
		const { brandUserId, currentSuspended, offset, size, searchParam, onSuccess, onError } = action.payload;

		const url = ApiConstants.USERS.UPDATE_STATUS.URL(brandUserId, !currentSuspended);
		return http.put(url, {})
			.then(() => {
				if (onSuccess) onSuccess();
				// Re-fetch users to reflect changes
				dispatch({
					type: ActionConstants.UsersConstants.GET_ALL_USERS,
					payload: { offset, size, searchParam }
				});
				done();
			})
			.catch((err) => {
				const message = extractErrorMessage(err, 'Failed to update user status');
				if (onError) onError(message);
				console.error('Error updating user status:', err);
				done();
			});
	}
});

const deleteUserLogic = createLogic({
	type: ActionConstants.UsersConstants.DELETE_USER,
	latest: true,
	process({ action }, dispatch, done) {
		const { brandUserId, offset, size, searchParam, onSuccess, onError } = action.payload;

		const url = ApiConstants.USERS.DELETE_USER.URL(brandUserId);
		return http.delete(url)
			.then(() => {
				if (onSuccess) onSuccess();
				// Re-fetch users to reflect changes
				dispatch({
					type: ActionConstants.UsersConstants.GET_ALL_USERS,
					payload: { offset, size, searchParam }
				});
				done();
			})
			.catch((err) => {
				const message = extractErrorMessage(err, 'Failed to delete user');
				if (onError) onError(message);
				console.error('Error deleting user:', err);
				done();
			});
	}
});

/**
 * createUserLogic
 *
 * Flow:
 *   1. POST /brands/:brandId/users/create  (synchronous — need brandUserId for step 2)
 *   2. If isCreated === false  →  dispatch error (user already exists), done
 *   3. Promise.allSettled([brandAccess, role, permissions])  — parallel
 *   4. Dispatch SUCCESS. For each failed secondary call, invoke onSecondaryError so
 *      the modal can show individual error toasts. Refresh user list.
 *
 * action.payload shape:
 *   { basicPayload, brandAccessPayload, rolePayload, permissionsPayload,
 *     offset, size, searchParam, onSuccess, onError, onSecondaryError }
 */
const createUserLogic = createLogic({
	type: ActionConstants.UsersConstants.CREATE_USER,
	async process({ action }, dispatch, done) {
		const {
			basicPayload,
			brandAccessPayload,
			rolePayload,
			permissionsPayload,
			offset,
			size,
			searchParam,
			onSuccess,
			onError,
			onSecondaryError,
		} = action.payload;

		dispatch({ type: ActionConstants.UsersConstants.CREATE_USER_LOADING });

		try {
			const createUrl = ApiConstants.USERS.CREATE_USER_COMPLETE.URL();
			const payload = {
				basic: basicPayload,
				brandAccess: brandAccessPayload,
				role: rolePayload,
				permissions: permissionsPayload,
			};
			const createRes = await http.post(createUrl, {}, payload);
			const result = createRes?.result || createRes;

			// Backend may return isCreated: false when the user already exists
			if (result?.isCreated === false) {
				const message = result?.message || 'A user with this email or mobile already exists.';
				dispatch({
					type: ActionConstants.UsersConstants.CREATE_USER_ERROR,
					payload: message,
				});
				if (onError) onError(message);
				done();
				return;
			}

			const brandUserId = result?.brandUserId;

			if (!brandUserId) {
				const message = 'User creation failed: no brandUserId returned.';
				dispatch({ type: ActionConstants.UsersConstants.CREATE_USER_ERROR, payload: message });
				if (onError) onError(message);
				done();
				return;
			}

			// Report individual secondary failures via onSecondaryError callback
			const secondaryResults = result?.secondaryResults || {};
			Object.entries(secondaryResults).forEach(([key, res]) => {
				if (!res.success && onSecondaryError) {
					onSecondaryError(key, res.error || 'Unknown error');
				}
			});

			// User was created — always dispatch success so modal knows it
			dispatch({ type: ActionConstants.UsersConstants.CREATE_USER_SUCCESS });
			if (onSuccess) onSuccess(brandUserId);

			// Refresh the user list
			dispatch({
				type: ActionConstants.UsersConstants.GET_ALL_USERS,
				payload: { offset, size, searchParam },
			});
		} catch (err) {
			const message = extractErrorMessage(err, 'Failed to create user');
			dispatch({ type: ActionConstants.UsersConstants.CREATE_USER_ERROR, payload: message });
			if (onError) onError(message);
		}

		done();
	},
	warnTimeout: 0,
});

/**
 * editUserLogic
 *
 * All four calls fire simultaneously via Promise.allSettled:
 *   1. PUT /users/:brandUserId           (basic details)
 *   2. PUT /users/:brandUserId/brandAccess
 *   3. PUT /users/:brandUserId/permissions  (role)
 *   4. PUT /users/:brandUserId/permissions  (individual permission accesses)
 *
 * action.payload shape:
 *   { brandUserId, basicPayload, brandAccessPayload, rolePayload, permissionsPayload,
 *     offset, size, searchParam, onSuccess, onError, onSecondaryError }
 */
const editUserLogic = createLogic({
	type: ActionConstants.UsersConstants.EDIT_USER,
	async process({ action }, dispatch, done) {
		const {
			brandUserId,
			basicPayload,
			brandAccessPayload,
			rolePayload,
			permissionsPayload,
			roleDeletePayload,
			offset,
			size,
			searchParam,
			onSuccess,
			onError,
			onSecondaryError,
		} = action.payload;

		dispatch({ type: ActionConstants.UsersConstants.EDIT_USER_LOADING });

		try {
			const updateUrl = ApiConstants.USERS.UPDATE_USER_COMPLETE.URL(brandUserId);
			const payload = {
				basic: basicPayload,
				brandAccess: brandAccessPayload,
				role: rolePayload,
				roleDelete: roleDeletePayload,
				permissions: permissionsPayload,
			};
			const editRes = await http.put(updateUrl, {}, payload);
			const result = editRes?.result || editRes;

			// Report individual secondary failures
			const secondaryResults = result?.secondaryResults || {};
			Object.entries(secondaryResults).forEach(([key, res]) => {
				if (!res.success && onSecondaryError) {
					onSecondaryError(key, res.error || 'Unknown error');
				}
			});

			dispatch({ type: ActionConstants.UsersConstants.EDIT_USER_SUCCESS });
			if (onSuccess) onSuccess();

			// Refresh the user list
			dispatch({
				type: ActionConstants.UsersConstants.GET_ALL_USERS,
				payload: { offset, size, searchParam },
			});
		} catch (err) {
			const message = extractErrorMessage(err, 'Failed to update user');
			dispatch({ type: ActionConstants.UsersConstants.EDIT_USER_ERROR, payload: message });
			if (onError) onError(message);
		}

		done();
	},
	warnTimeout: 0,
});

/**
 * getUserLogic
 *
 * Fetches full user details for edit pre-population.
 * Fires three GET calls simultaneously:
 *   1. GET /user/:brandUserId              (basic details)
 *   2. GET /users/:brandUserId/permissions (brand accesses + individual permissions)
 *   3. GET /users/:brandUserId/roleGroups/:brandId  (assigned role group)
 *
 * Resolves with a merged userData object for the modal to pre-populate.
 */
const getUserLogic = createLogic({
	type: ActionConstants.UsersConstants.GET_USER,
	latest: true,
	async process({ action }, dispatch, done) {
		const { brandUserId } = action.payload;

		dispatch({ type: ActionConstants.UsersConstants.GET_USER_LOADING });

		const brandId = window?.LTD?.getSiteInfo?.()?.brandId || '1672';

		try {
			// Use allSettled so a single failing call does not abort the other two in-flight requests
			const [userResult, permissionsResult, roleGroupsResult] = await Promise.allSettled([
				http.get(ApiConstants.USERS.GET_USER.URL(brandUserId), {}),
				http.get(ApiConstants.USERS.GET_USER_PERMISSIONS.URL(brandUserId), {}),
				http.get(ApiConstants.USERS.GET_USER_ROLE_GROUPS.URL(brandUserId, brandId), {}),
			]);

			// If basic user details failed, bail out — we cannot pre-populate anything
			if (userResult.status === 'rejected') {
				throw userResult.reason;
			}

			const userRes = userResult.value;
			const permissionsRes = permissionsResult.status === 'fulfilled' ? permissionsResult.value : null;
			const roleGroupsRes = roleGroupsResult.status === 'fulfilled' ? roleGroupsResult.value : null;

			const userDetails = userRes?.result || {};
			const permissionsData = permissionsRes?.result || {};
			const roleGroupsData = Array.isArray(roleGroupsRes?.result) ? roleGroupsRes.result : [];

			// Build brand access map: { [brandId]: [outletId, ...] }
			// The API returns userBrandOutletAccesses already in this exact shape:
			//   { "1672": ["41033", "41034"], "ALL": ["ALL"] }
			// Values are plain arrays, not objects — no Object.keys() needed.
			const rawBrandAccesses = permissionsData?.userBrandOutletAccesses || {};
			const brandAccessOutlets = {};
			Object.entries(rawBrandAccesses).forEach(([bId, outletArr]) => {
				if (Array.isArray(outletArr) && outletArr.length > 0) {
					// Normalise: keep all string outletIds as-is
					brandAccessOutlets[bId] = outletArr.map(String);
				}
			});

			// Extract individual permission role IDs.
			// result.permissions is an array shaped like unmappedPermissions:
			//   [{ productIdentifier, featureIdentifier, accesses: [{ roleId, roleName, ... }] }]
			// We collect all roleIds from all accesses entries.
			const rawPermissions = Array.isArray(permissionsData?.permissions)
				? permissionsData.permissions
				: [];
			const selectedPermissionRoleIds = [];
			rawPermissions.forEach((permEntry) => {
				(permEntry.accesses || []).forEach((access) => {
					if (access.roleId != null && (!access.roleGroupId || String(access.roleGroupId) === '0')) {
						selectedPermissionRoleIds.push(String(access.roleId));
					}
				});
			});

			// Extract role group ID (first role if any)
			const assignedRole = Array.isArray(roleGroupsData) && roleGroupsData.length > 0
				? String(roleGroupsData[0].roleGroupId || '')
				: '';

			const mergedUserData = {
				// Basic details
				name: userDetails.fullName || '',
				email: userDetails.primaryEmail || '',
				countryCode: userDetails.countryCode || '',
				mobile: String(userDetails.primaryMobile || ''),
				gender: userDetails.gender || 'NOT_SHARED',
				dob: userDetails.dob || null,
				source: userDetails.source || '0',
				type: userDetails.type || 'USER',
				status: userDetails.status || 'ACTIVE',
				// Relational data
				brandAccessOutlets,
				selectedRole: assignedRole,
				selectedPermissionRoleIds,
				// Keep original IDs for API calls
				brandUserId: userDetails.brandUserId,
				userId: userDetails.userId,
			};

			dispatch({
				type: ActionConstants.UsersConstants.GET_USER_SUCCESS,
				payload: mergedUserData,
			});
		} catch (err) {
			const message = extractErrorMessage(err, 'Failed to fetch user details');
			dispatch({ type: ActionConstants.UsersConstants.GET_USER_ERROR, payload: message });
			console.error('Error fetching user details:', err);
		}

		done();
	},
	warnTimeout: 0,
});

const UsersActions = [
	getAllUsersLogic,
	updateUserStatusLogic,
	deleteUserLogic,
	createUserLogic,
	editUserLogic,
	getUserLogic,
];

export default UsersActions;